import Phaser from 'phaser';
import type { SongResult } from '@/types';
import { getSongById } from '@/data/songs';
import { getBackgroundById } from '@/data/backgrounds';
import type { SaveManager } from '@/game/systems/SaveManager';
import type { AudioManager } from '@/game/systems/AudioManager';
import type { GameplayScene } from './GameplayScene';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { FONT_FAMILY, PALETTE, animateCoinSpend, bodyTextStyle, createButton, createCoinPanel, titleTextStyle } from '@/game/ui/theme';
import { Background } from '@/game/objects/Background';
import { spawnBurst } from '@/utils/effects';

const CONTINUE_COST = 200;

export interface ResultsSceneData {
  result: SongResult;
  /** Present only when reached via a fail -- GameplayScene paused itself (rather
   * than stopping) so a paid Continue can resume from the exact failure moment.
   * Absent for a normal song-complete result, where GameplayScene has already been
   * fully torn down by the time Results appears. */
  gameplaySceneKey?: string;
}

export class ResultsScene extends Phaser.Scene {
  private result!: SongResult;
  private gameplaySceneKey?: string;
  /** Guards payToContinue against firing more than once for a single tap -- e.g. a
   * touch input dispatching both a pointer and a synthetic mouse event on the same
   * overlapping button. A purchase must never be double-charged. */
  private continuePurchasePending = false;

  constructor() {
    super('ResultsScene');
  }

  init(data: ResultsSceneData): void {
    this.result = data.result;
    this.gameplaySceneKey = data.gameplaySceneKey;
    // Phaser reuses one ResultsScene instance across every visit rather than
    // reconstructing it, so a field initializer alone only resets this once ever --
    // it must be reset here too, or a second fail-and-continue in the same session
    // finds this still true from the first and payToContinue() silently no-ops.
    this.continuePurchasePending = false;
  }

  create(): void {
    const song = getSongById(this.result.songId);
    const saveManager = this.game.registry.get('saveManager') as SaveManager;
    const audioManager = this.game.registry.get('audioManager') as AudioManager;
    const backgroundId = saveManager.getSelectedBackgroundId() ?? song.background;
    new Background(this, getBackgroundById(backgroundId));

    if (this.result.failed) {
      this.add.text(INTERNAL_WIDTH / 2, 90, 'Oh No!', titleTextStyle(46)).setOrigin(0.5);
      this.add.text(INTERNAL_WIDTH / 2, 150, 'A treat got away -- try again!', bodyTextStyle(24, '#ffb3b3')).setOrigin(0.5);
    } else {
      this.add.text(INTERNAL_WIDTH / 2, 90, 'Song Complete!', titleTextStyle(46)).setOrigin(0.5);
      this.add.text(INTERNAL_WIDTH / 2, 150, song.title, bodyTextStyle(26, '#cbb9e8')).setOrigin(0.5);
      this.drawStars(INTERNAL_WIDTH / 2, 230, this.result.stars);
    }

    if (this.result.isNewHighScore) {
      this.add
        .text(INTERNAL_WIDTH / 2, 290, '✨ New High Score! ✨', bodyTextStyle(24, '#ffe066'))
        .setOrigin(0.5);
    }

    const panelX = INTERNAL_WIDTH / 2 - 300;
    const panelY = 330;
    const panel = this.add.graphics();
    panel.fillStyle(PALETTE.panel, 0.92);
    panel.fillRoundedRect(panelX, panelY, 600, 400, 28);

    const rows: [string, string][] = [
      ['Score', this.result.score.toLocaleString()],
      ['Accuracy', `${this.result.accuracy.toFixed(1)}%`],
      ['Perfect', `${this.result.perfectCount}`],
      ['Good', `${this.result.goodCount}`],
      ['Miss', `${this.result.missCount}`],
      ['Max Combo', `${this.result.maxCombo}x`],
    ];

    rows.forEach(([label, value], i) => {
      const y = panelY + 40 + i * 48;
      this.add.text(panelX + 40, y, label, bodyTextStyle(26)).setOrigin(0, 0.5);
      this.add.text(panelX + 560, y, value, bodyTextStyle(26, '#ffe066')).setOrigin(1, 0.5);
    });

    if (this.result.failed) {
      this.buildFailedButtons(saveManager, audioManager);
    } else {
      const retryBtn = createButton(this, INTERNAL_WIDTH / 2 - 170, INTERNAL_HEIGHT - 175, 300, 84, 'Retry', PALETTE.secondary);
      const continueBtn = createButton(this, INTERNAL_WIDTH / 2 + 170, INTERNAL_HEIGHT - 175, 300, 84, 'Continue', PALETTE.primary);
      retryBtn.on('pointerup', () =>
        this.scene.start('GameplayScene', { songId: this.result.songId, difficulty: this.result.difficulty })
      );
      continueBtn.on('pointerup', () => this.scene.start('SongSelectScene'));

      this.celebrateCoinsEarned(saveManager, audioManager);
    }
  }

  /** A deliberately bigger moment than the rest of the results panel: after a short
   * beat (so the score/stars register first), a coin icon pops in and the award
   * amount counts up from zero with a triumphant fanfare and a sparkle burst, and
   * the running coin total top-right ticks up to match -- finishing a level should
   * feel like a reward, not just another line in a stats table. */
  private celebrateCoinsEarned(saveManager: SaveManager, audioManager: AudioManager): void {
    const coinsAwarded = this.result.coinsAwarded;
    if (coinsAwarded <= 0) return;

    const totalAfter = saveManager.getData().coins;
    const totalBefore = totalAfter - coinsAwarded;
    const totalCoinText = createCoinPanel(this, INTERNAL_WIDTH - 110, 44, totalBefore);

    const centerX = INTERNAL_WIDTH / 2;
    const centerY = 860;
    const celebration = this.add.container(centerX, centerY).setScale(0);
    const iconX = -110;
    const icon = this.add.circle(iconX, 0, 38, 0xffe066).setStrokeStyle(5, 0xffffff);
    const iconShine = this.add.circle(iconX - 12, -12, 10, 0xfff6d6, 0.9);
    const counterText = this.add
      .text(-50, -6, '+0', { ...titleTextStyle(58), color: '#ffe066' })
      .setOrigin(0, 0.5);
    const label = this.add.text(-50, 46, 'coins earned!', bodyTextStyle(20, '#e9d9ff')).setOrigin(0, 0.5);
    celebration.add([icon, iconShine, counterText, label]);

    this.time.delayedCall(500, () => {
      audioManager.playCoinCelebration();
      spawnBurst(this, centerX + iconX, centerY, PALETTE.accent, 22);

      this.tweens.add({
        targets: celebration,
        scale: { from: 0, to: 1 },
        duration: 550,
        ease: 'Back.easeOut',
      });

      const counter = { value: 0 };
      this.tweens.add({
        targets: counter,
        value: coinsAwarded,
        duration: 1000,
        ease: 'Cubic.easeOut',
        onUpdate: () => counterText.setText(`+${Math.round(counter.value)}`),
      });

      animateCoinSpend(this, totalCoinText, totalBefore, totalAfter, () => {});
    });
  }

  /** The fail screen gets a third option above the usual Retry/Quit row: pay to
   * resume the same run from the exact moment it failed (GameplayScene is still
   * alive, just paused -- see failLevel()/continueAfterPayment()). */
  private buildFailedButtons(saveManager: SaveManager, audioManager: AudioManager): void {
    const coinText = createCoinPanel(this, INTERNAL_WIDTH - 110, 44, saveManager.getData().coins);

    const canAfford = saveManager.getData().coins >= CONTINUE_COST;
    const continueBtn = createButton(
      this,
      INTERNAL_WIDTH / 2,
      INTERNAL_HEIGHT - 275,
      600,
      84,
      `Continue - ${CONTINUE_COST} coins`,
      canAfford ? PALETTE.success : PALETTE.panelLight,
      26
    );
    if (!canAfford) {
      continueBtn.disableInteractive();
      continueBtn.setAlpha(0.5);
    } else {
      continueBtn.on('pointerup', () => this.payToContinue(saveManager, audioManager, coinText));
    }

    const retryBtn = createButton(this, INTERNAL_WIDTH / 2 - 170, INTERNAL_HEIGHT - 175, 300, 84, 'Retry', PALETTE.primary);
    const quitBtn = createButton(this, INTERNAL_WIDTH / 2 + 170, INTERNAL_HEIGHT - 175, 300, 84, 'Quit', PALETTE.secondary);

    retryBtn.on('pointerup', () => {
      if (this.gameplaySceneKey) this.scene.stop(this.gameplaySceneKey);
      this.scene.start('GameplayScene', { songId: this.result.songId, difficulty: this.result.difficulty });
    });
    quitBtn.on('pointerup', () => {
      if (this.gameplaySceneKey) this.scene.stop(this.gameplaySceneKey);
      this.scene.start('SongSelectScene');
    });
  }

  private payToContinue(saveManager: SaveManager, audioManager: AudioManager, coinText: Phaser.GameObjects.Text): void {
    if (this.continuePurchasePending) return;
    const before = saveManager.getData().coins;
    if (!saveManager.spendCoins(CONTINUE_COST)) return; // button is gated on affordability already
    this.continuePurchasePending = true;
    audioManager.playUnlock();

    animateCoinSpend(this, coinText, before, saveManager.getData().coins, () => {
      this.children.removeAll(true);
      this.startResumeCountdown();
    });
  }

  /** Big 3-2-1 countdown so the player has a moment to get their fingers back in
   * position before the still-paused song (and falling notes) resume. Deliberately
   * leaves no opaque backdrop of its own: GameplayScene sits paused underneath this
   * overlay scene (see failLevel()) and beginRepositioning() below hands its
   * update/input loop back just for this window, so the player can actually see and
   * drag their cats into place instead of staring at a blank screen while they wait. */
  private startResumeCountdown(): void {
    if (this.gameplaySceneKey) {
      (this.scene.get(this.gameplaySceneKey) as GameplayScene).beginRepositioning();
    }

    const readyText = this.add
      .text(INTERNAL_WIDTH / 2, INTERNAL_HEIGHT / 2 - 140, 'READY?', {
        fontFamily: FONT_FAMILY,
        fontSize: '84px',
        fontStyle: 'italic',
        color: '#ffe066',
        stroke: '#ff5fa2',
        strokeThickness: 10,
      })
      .setOrigin(0.5)
      .setShadow(0, 6, '#3a1655', 6, false, true)
      .setScale(0);
    this.tweens.add({
      targets: readyText,
      scale: 1,
      angle: { from: -6, to: 0 },
      duration: 420,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: readyText,
      scaleX: { from: 1, to: 1.06 },
      scaleY: { from: 1, to: 0.94 },
      duration: 520,
      delay: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const countText = this.add.text(INTERNAL_WIDTH / 2, INTERNAL_HEIGHT / 2 + 40, '3', titleTextStyle(160)).setOrigin(0.5);

    let remaining = 3;
    this.time.addEvent({
      delay: 1000,
      repeat: 2,
      callback: () => {
        remaining -= 1;
        if (remaining > 0) {
          countText.setText(`${remaining}`);
          this.tweens.add({ targets: countText, scale: { from: 1.4, to: 1 }, duration: 260, ease: 'Back.easeOut' });
        } else {
          this.resumeGameplay();
        }
      },
    });
  }

  private resumeGameplay(): void {
    if (this.gameplaySceneKey) {
      (this.scene.get(this.gameplaySceneKey) as GameplayScene).continueAfterPayment();
    }
    this.scene.stop();
  }

  private drawStars(centerX: number, y: number, filledCount: number): void {
    const spacing = 90;
    for (let i = 0; i < 3; i++) {
      const x = centerX + (i - 1) * spacing;
      const filled = i < filledCount;
      this.add
        .star(x, y, 5, filled ? 26 : 22, filled ? 46 : 40, filled ? 0xffe066 : 0x554477, 1)
        .setStrokeStyle(3, filled ? 0xffffff : 0x3a2a5e);
    }
  }
}
